import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';

import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { SocketService } from 'app/main/apps/chat/socket.service';
import { CustomerAccount } from 'app/main/pages/authentication/shared/model/user.model';
import { LocalStorageService } from 'app/services/local-storage.service';
import { NavigationRoomStatusService } from './navigation-room-status.service';

@Component({
  selector: 'fuse-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FuseNavigationComponent implements OnInit {
  @Input()
  layout = 'vertical';
  @Input()
  navigation: any;
  userProfile: CustomerAccount;
  roomTypes = [
    { name: 'whatsapp', value: 1 },
    { name: 'web', value: 3 },
    { name: 'messenger', value: 2 },
    { name: 'email', value: 5 },
    { name: 'instagram', value: 6 },
    { name: 'whatsapp_lite', value: 7 },
  ];

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   *
   * @param {ChangeDetectorRef} _changeDetectorRef
   * @param {FuseNavigationService} _fuseNavigationService
   */
  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _fuseNavigationService: FuseNavigationService,
    private socketService: SocketService,
    private _localStorageService: LocalStorageService,
    private navigationRoomStatusService: NavigationRoomStatusService
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
    this.getUserProfile();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to the current navigation changes
    this._fuseNavigationService.onNavigationChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(navigationKey => {
        // Load the navigation
        this.navigation = navigationKey
          ? this._fuseNavigationService.getCurrentNavigation()
          : null;

        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    // Subscribe to navigation item
    merge(
      this._fuseNavigationService.onNavigationItemAdded,
      this._fuseNavigationService.onNavigationItemUpdated,
      this._fuseNavigationService.onNavigationItemRemoved
    )
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    this.subscribeToRoomEvents();
  }

  private subscribeToRoomEvents(): void {
    this.roomTypes.forEach(element => {
      this.socketService[`${element.name}` + 'MethodCallSource']
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(msj => {
          if (msj) {
            const currentUser = this.userProfile;
            this.navigationRoomStatusService.notifyChatsStatus(msj);

            if (
              msj.chatMessage &&
              `${msj.chatMessage.customerId}` === `${currentUser.customerId}`
            ) {
              const { messageType, text, chat } = msj.chatMessage;

              if (`${messageType}`.trim() === 'OUT') {
                this.handleOutgoingMessage(text, chat);
              }
            }
          }
        });
    });
  }

  handleOutgoingMessage(text: string, chat: any): void {
    if (text === 'change_rooms_assigned') {
      this._fuseNavigationService.getRooms();
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  getUserProfile(): void {
    this.userProfile = this._localStorageService.getCurrentUser();
  }
}
